import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Injector,
  input,
  Input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommentApiService } from '@shared/api/api-loop/services/comment-api.service';
import { CommentChat } from '@shared/api/api-loop/models/comment-chat';
import { BBCodePipe } from '@shared/modules/comments/components/pipes/bbcode/bbcode.pipe';
import { CommentReactionsComponent } from '@shared/modules/comments/components/comment-reactions/comment-reactions.component';
import { processBodyDeeplinkClick } from '@shared/modules/deeplinks/common/helpers/process-body-deeplink-click';
import { DateFormat } from '@shared/shared/fancy-date/common/enumerators/date-format';
import { ToastService } from '@shared/ui/kit/toast/toast.service';
import { EnumObjectValue } from '@shared/utils/common/types';
import { CommentActionsService } from '@shared/modules/comments/components/services/comment-actions/comment-actions.service';
import { IS_EDITED_INDICATOR_VISIBLE } from '@shared/modules/comments/common/injection-tokens/is-edited-indicator-visible.token';
import { SharedTagActionsService } from '@shared/modules/shared-tag/data-access/shared-tag-actions/shared-tag-actions.service';
import { LoopEmojiDirective } from '@shared/ui/emoji/directives/emoji.directive';
import { CommentOperationsService } from '@shared/modules/comments/components/services/comment-operations.service';
import { ElectronService } from '@shared/services/electron/electron';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { commentBodyToPlainText } from '@shared/modules/comments/common/helpers/comment-body-to-plain-text';
import { CommentChatModel, CommentModel, isSystemComment } from '@dta/shared/models-api-loop/comment/comment.model';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { differenceInMinutes, parseISO } from 'date-fns';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { writeTextToClipboard } from '@dta/shared/clipboard/write-text-to-clipboard';
import { BBCodeService } from '@dta/shared/services/bbcode/bbcode.service';

export const AvailableCommentActions = {
  delete: 'delete',
  edit: 'edit',
  reply: 'reply',
  reaction: 'reaction',
  copy: 'copy'
};

export interface CommentAction {
  actionFn: () => void;
  icon: string;
}

@UntilDestroy()
@Component({
  selector: 'loop-chat-comment',
  templateUrl: './chat-comment.component.html',
  styleUrls: ['./chat-comment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BBCodePipe],
  standalone: false,
  host: {
    '[class.me]': 'isMyCommentSignal()'
  }
})
export class ChatCommentComponent implements OnDestroy, OnInit {
  private readonly commentApiService: CommentApiService = inject(CommentApiService);
  private readonly electronService: ElectronService = inject(ElectronService);
  private readonly injector: Injector = inject(Injector);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly commentActionsService: CommentActionsService = inject(CommentActionsService);
  private readonly commentOperationsService: CommentOperationsService = inject(CommentOperationsService);
  private readonly sharedTagActionsService: SharedTagActionsService = inject(SharedTagActionsService);
  private readonly userAuthService: UserAuthService = inject(UserAuthService);
  private readonly bbCodeService: BBCodeService = inject(BBCodeService);
  protected readonly isEditedIndicatorVisible: boolean = inject(IS_EDITED_INDICATOR_VISIBLE);

  @HostBinding('class.sameAuthor') IsSameAuthor: boolean = false;

  protected readonly AvailableCommentActions: typeof AvailableCommentActions = AvailableCommentActions;
  protected readonly DateFormat_RelativeNowOffsetShort: DateFormat.relativeNowOffsetShort =
    DateFormat.relativeNowOffsetShort;

  protected readonly comment$: ReplaySubject<CommentChatModel> = new ReplaySubject<CommentChatModel>(1);
  @Input() set comment(comment: CommentChatModel) {
    this.comment$.next(comment);
  }

  protected readonly previousComment$: ReplaySubject<CommentModel | null> = new ReplaySubject<CommentModel | null>(1);
  @Input() set previousComment(previousComment: CommentModel | null) {
    this.previousComment$.next(previousComment);
  }

  protected readonly isMyCommentSignal: Signal<boolean> = toSignal(
    combineLatest([
      this.comment$,
      this.userAuthService.currentSession$.pipe(
        map(session => session.user.id),
        distinctUntilChanged()
      )
    ]).pipe(map(([comment, userId]) => comment?.author?.id === userId)),
    { initialValue: false }
  );

  protected readonly isCollapsed$: ReplaySubject<{ isCollapsed: boolean }> = new ReplaySubject<{
    isCollapsed: boolean;
  }>(1);
  @Input() set isCollapsed(isCollapsed: boolean) {
    this.isCollapsed$.next({ isCollapsed: isCollapsed });
  }
  @Input() conversationId?: string;

  @Input() availableActions: EnumObjectValue<typeof AvailableCommentActions>[] = [
    AvailableCommentActions.copy,
    AvailableCommentActions.reaction,
    AvailableCommentActions.edit,
    AvailableCommentActions.delete,
    AvailableCommentActions.reply
  ];

  readonly additionalActionsSignal: InputSignal<CommentAction[]> = input([], { alias: 'additionalActions' });

  @Output() editComment: EventEmitter<CommentChatModel> = new EventEmitter<CommentChatModel>();
  @Output() replyComment: EventEmitter<CommentChatModel> = new EventEmitter<CommentChatModel>();
  @Output() retryComment: EventEmitter<CommentChatModel> = new EventEmitter<CommentChatModel>();

  private readonly LoopEmojiDirectiveSignal: Signal<LoopEmojiDirective> = viewChild(LoopEmojiDirective);

  private readonly commentReactionsRefSignal: Signal<CommentReactionsComponent | undefined> =
    viewChild(CommentReactionsComponent);

  protected readonly commentErrorSignal: Signal<string | null> = toSignal(
    this.comment$.pipe(
      switchMap(comment => {
        return this.commentActionsService.batchCommentErrors$.pipe(
          map(commentErrors =>
            commentErrors.find(commentError => [comment.id, comment._id].includes(commentError.commentId))
          ),
          map(commentError => commentError?.errorMessage ?? null)
        );
      })
    ),
    { initialValue: null }
  );

  protected readonly quoteComment$: Observable<CommentModel | null> = this.comment$.pipe(
    switchMap(comment => {
      if (!comment.quoteCommentId) {
        return of(null);
      }
      return this.commentApiService
        .Comment_Get(
          { id: comment.quoteCommentId },
          {
            isCacheEnabled: true
          }
        )
        .pipe(
          map(comment => {
            return CommentChatModel.create(comment);
          })
        );
    })
  );

  protected readonly isSameAuthorAsPreviousComment$: Observable<boolean> = combineLatest([
    this.comment$,
    this.previousComment$
  ]).pipe(
    map(([comment, previousComment]) => {
      return (
        previousComment && comment.$type === previousComment.$type && comment.author?.id === previousComment.author?.id
      );
    })
  );

  protected readonly isCommentAuthorVisible$: Observable<boolean> = combineLatest([
    this.isSameAuthorAsPreviousComment$,
    this.comment$,
    this.previousComment$
  ]).pipe(
    map(([isSameAuthorAsPreviousComment, comment, previousComment]) => {
      if (!previousComment) {
        return true;
      }

      if (!isSameAuthorAsPreviousComment) {
        return true;
      }

      if (isSystemComment(previousComment)) {
        return true;
      }

      return differenceInMinutes(parseISO(comment.created), parseISO(previousComment.created)) > 5;
    })
  );

  ngOnInit(): void {
    this.observeCommentWithoutBody();
    this.observeSharedTagChanges();
    this.observeCommentChanges();
  }

  private observeCommentChanges(): void {
    this.comment$
      .pipe(
        switchMap(comment => {
          return this.commentActionsService.commentsUpdatedWithSync$.pipe(
            map(commentsUpdatedWithSync =>
              commentsUpdatedWithSync.find(updatedComment => updatedComment.id === comment.id)
            ),
            filter(comment => {
              return !!comment;
            })
          );
        }),

        untilDestroyed(this)
      )
      .subscribe(updatedComment => {
        this.comment$.next(new CommentChatModel(updatedComment));
      });
  }

  private observeSharedTagChanges(): void {
    this.comment$
      .pipe(
        switchMap(comment => {
          return this.sharedTagActionsService.sharedTagsUpdated$.pipe(
            filter(listOfTags => listOfTags.parent?.id === comment.id),
            map(listOfTags => {
              return {
                listOfTags,
                comment
              };
            })
          );
        }),
        untilDestroyed(this)
      )
      .subscribe(({ listOfTags, comment }) => {
        comment.sharedTags = listOfTags;
        this.comment$.next(new CommentChatModel(comment));
      });
  }

  private observeCommentWithoutBody(): void {
    combineLatest([
      this.isCollapsed$,
      this.comment$.pipe(distinctUntilChanged((prevComment, currComment) => prevComment?.id === currComment?.id))
    ])
      .pipe(
        switchMap(([isCollapsed, comment]) => {
          if (isCollapsed.isCollapsed || typeof comment.comment === 'string') {
            return of(comment);
          }
          return this.commentApiService
            .Comment_Get({
              id: comment.id
            })
            .pipe(
              map((loadedComment: CommentChat) => {
                comment.comment = loadedComment.comment;
                return comment;
              })
            );
        }),
        untilDestroyed(this)
      )
      .subscribe(comment => {
        this.comment$.next(comment);
      });
  }

  protected copyComment(comment: CommentChatModel): void {
    if (comment.comment || !comment.attachments?.resources?.length) {
      writeTextToClipboard(commentBodyToPlainText(comment.comment, this.bbCodeService));
      this.toastService.info({ title: 'Comment copied successfully' });
    } else {
      this.electronService
        .saveFileToClipboard$(comment.attachments.resources[0])
        .pipe(take(1), untilDestroyed(this))
        .subscribe(result => {
          // The error toast is raised by the electron service, so only confirm an actual success here.
          if (!result?.errorMessage) {
            this.toastService.info({ title: 'Attachment copied successfully' });
          }
        });
    }
  }

  protected handleRetryComment(comment: CommentChatModel): void {
    this.commentActionsService.commentError$.next({ commentId: comment._id, errorMessage: null });
    this.retryComment.emit(comment);
  }

  protected handleEmojiReaction(emoji: string): void {
    this.commentReactionsRefSignal()?.toggleReaction(emoji);
  }

  deleteComment(comment: CommentChat): void {
    this.commentOperationsService.deleteComment$(comment).pipe(untilDestroyed(this)).subscribe();
  }

  handleBodyClick($event): void {
    processBodyDeeplinkClick($event, this.injector);
  }

  bodyCopy($event): void {
    $event.clipboardData.setData('text/plain', window.getSelection().toString());
    $event.preventDefault();
  }

  ngOnDestroy(): void {
    this.comment$.complete();
    this.previousComment$.complete();
    this.isCollapsed$.complete();
  }
}
